import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { getCategory } from '../../graphql/queries';
import { updateCategory, deleteCategory, updateTool, reorderTools } from '../../graphql/mutation';
import { 
  Card, 
  Form, 
  Input, 
  InputNumber,
  Button, 
  Space, 
  Row, 
  Col,
  Descriptions,
  Modal,
  Divider,
  Table,
  Avatar,
  Radio,
  Select
} from 'antd';
import { SaveOutlined, DeleteOutlined, EditOutlined, HolderOutlined } from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { showSnackBar } from '../../components/SnackBar/SnackBar';
import './CategoryDetail.css';

const { Option } = Select;

const { TextArea } = Input;

// Draggable Row Component
const DraggableRow = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </tr>
  );
};

const CategoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [sortedTools, setSortedTools] = useState([]);

  const { data: categoryData, loading: fetchLoading } = useQuery(getCategory, {
    variables: { categoryId: id },
  });

  const [updateCategoryMutation] = useMutation(updateCategory);
  const [deleteCategoryMutation] = useMutation(deleteCategory);
  const [updateToolMutation] = useMutation(updateTool);
  const [reorderToolsMutation] = useMutation(reorderTools);
  const isReorderingRef = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  useEffect(() => {
    if (categoryData?.getCategory) {
      const category = categoryData.getCategory;
      form.setFieldsValue({
        name: category.name,
        description: category.description,
        rowNumber: category.rowNumber,
        skipTranslation: category.skipTranslation ?? false,
        type: category.type,
      });

      if (!isReorderingRef.current) {
        if (category.tools && category.tools.length > 0) {
          const sorted = [...category.tools].sort((a, b) => a.rowNumber - b.rowNumber);
          setSortedTools(sorted);
        } else {
          setSortedTools([]);
        }
      }
    }
  }, [categoryData, form]);

  const handleUpdate = async (values) => {
    try {
      setLoading(true);

      await updateCategoryMutation({
        variables: {
          categoryId: id,
          name: values.name,
          description: values.description,
          rowNumber: values.rowNumber,
          skipTranslation: values.skipTranslation,
          type: values.type || null,
        }
      });

      showSnackBar('success', 'Success', 'Category updated successfully');
      setIsEditing(false);
      // Refetch the data to show updated values
      window.location.reload();
    } catch (error) {
      showSnackBar('error', 'Error', error.message || 'Failed to update category');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Category',
      content: 'Are you sure you want to delete this category? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteCategoryMutation({
            variables: { categoryId: id }
          });
          showSnackBar('success', 'Success', 'Category deleted successfully');
          navigate('/categories');
        } catch (error) {
          showSnackBar('error', 'Error', 'Failed to delete category');
        }
      }
    });
  };

  const onDragEnd = async ({ active, over }) => {
    if (isReorderingRef.current || active.id === over?.id) return;

    const activeIndex = sortedTools.findIndex((i) => i.id === active.id);
    const overIndex = sortedTools.findIndex((i) => i.id === over?.id);

    const reordered = arrayMove(sortedTools, activeIndex, overIndex).map((tool, index) => ({
      ...tool,
      rowNumber: index + 1,
    }));
    setSortedTools(reordered);
    isReorderingRef.current = true;

    try {
      await reorderToolsMutation({
        variables: { ids: reordered.map((t) => t.id) },
      });
      showSnackBar('success', 'Success', 'Tool order updated successfully');
    } catch (error) {
      showSnackBar('error', 'Error', 'Failed to update tool order');
      if (categoryData?.getCategory?.tools) {
        const sorted = [...categoryData.getCategory.tools].sort(
          (a, b) => a.rowNumber - b.rowNumber
        );
        setSortedTools(sorted);
      }
    } finally {
      isReorderingRef.current = false;
    }
  };

  const category = categoryData?.getCategory;

  if (fetchLoading) {
    return (
      <div className="category-detail-container">
        <Card loading />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="category-detail-container">
        <Card title="Category Not Found">
          <p>The requested category could not be found.</p>
          <Button onClick={() => navigate('/categories')}>
            Back to Categories
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="category-detail-container">
      <Card 
        title={`Category: ${category.name}`}
        extra={
          <Space>
            {!isEditing && (
              <Button 
                type="primary" 
                icon={<EditOutlined />}
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
            <Button 
              danger 
              icon={<DeleteOutlined />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Space>
        }
      >
        {!isEditing ? (
          <div className="category-details-view">
            <Descriptions column={1} bordered>
              <Descriptions.Item label="ID">{category.id}</Descriptions.Item>
              <Descriptions.Item label="Name">{category.name}</Descriptions.Item>
              <Descriptions.Item label="Description">{category.description}</Descriptions.Item>
              <Descriptions.Item label="Row Number">{category.rowNumber}</Descriptions.Item>
              <Descriptions.Item label="Content Type">{category.type || '—'}</Descriptions.Item>
              <Descriptions.Item label="Created At">
                {new Date(category.createdAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {new Date(category.updatedAt).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">Associated Tools (Drag to Reorder)</Divider>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={sortedTools.map((tool) => tool.id)}
                strategy={verticalListSortingStrategy}
              >
                <Table
                  dataSource={sortedTools}
                  rowKey="id"
                  pagination={false}
                  locale={{ emptyText: 'No tools associated with this category' }}
                  components={{
                    body: {
                      row: DraggableRow,
                    },
                  }}
                  columns={[
                    {
                      key: 'sort',
                      width: 50,
                      align: 'center',
                      render: () => <HolderOutlined style={{ cursor: 'move', color: '#999' }} />,
                    },
                    {
                      title: 'Thumbnail',
                      dataIndex: 'bannerUrl',
                      key: 'bannerUrl',
                      width: 100,
                      render: (bannerUrl, record) => (
                        <Avatar
                          src={bannerUrl}
                          shape="square"
                          size={64}
                          alt={record.name}
                        >
                          {!bannerUrl && record.name?.charAt(0).toUpperCase()}
                        </Avatar>
                      ),
                    },
                    {
                      title: 'Name',
                      dataIndex: 'name',
                      key: 'name',
                    },
                    {
                      title: 'Row Number',
                      dataIndex: 'rowNumber',
                      key: 'rowNumber',
                      width: 120,
                      align: 'center',
                    },
                  ]}
                />
              </SortableContext>
            </DndContext>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleUpdate}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Name"
                  rules={[{ required: true, message: 'Please enter category name' }]}
                >
                  <Input placeholder="Enter category name" />
                </Form.Item>

                <Form.Item
                  name="skipTranslation"
                  label="Translation"
                  initialValue={false}
                >
                  <Radio.Group>
                    <Radio value={false}>Auto translate</Radio>
                    <Radio value={true}>Do not translate (use original name)</Radio>
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  name="description"
                  label="Description"
                >
                  <TextArea 
                    rows={4} 
                    placeholder="Enter category description (optional)" 
                  />
                </Form.Item>

                <Form.Item
                  name="rowNumber"
                  label="Row Number"
                  rules={[{ required: true, message: 'Please enter row number' }]}
                >
                  <InputNumber
                    min={1}
                    placeholder="Enter row number"
                    style={{ width: '100%' }}
                  />
                </Form.Item>

                <Form.Item
                  name="type"
                  label="Content Type"
                  extra="Used by rule-based Tabs to filter categories (e.g. only VIDEO categories)"
                >
                  <Select placeholder="Select content type" allowClear>
                    <Option value="VIDEO">Video</Option>
                    <Option value="IMAGE">Image</Option>
                    <Option value="MIX">Mix</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  icon={<SaveOutlined />}
                >
                  Update Category
                </Button>
                <Button onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default CategoryDetail; 